import BaseService from "@/api/services/BaseService";
import type ServiceInterface from "@/api/services/ServiceInterface";
import type {
  AvailableEntity,
  ConfiguredEntity,
  DriverConnectionTestResult,
  DriverId,
  EntityRequest,
  IntegrationDriver,
  IntegrationDriverInfo,
  IntegrationDriverUpdate,
  IntegrationInstance,
  IntegrationRequest,
  IntegrationSetupData,
  IntegrationSetupDataInput,
  IntegrationSetupInfo,
  IntegrationStatus,
  NewIntegrationData,
} from "@/types/integrationInstance";
import type { MediaItem } from "@/types/media";
import type { RawAxiosRequestConfig } from "axios";
import { DriverType } from "@/types/enums";

const maxUploadTimeout = import.meta.env.VITE_API_MAX_IMPORT_INTEGRATON_TIMEOUT || 180000;

export default class ServiceIntegrations
  extends BaseService
  implements ServiceInterface
{

  async getConfiguredEntityNumber(): Promise<number> {
    const response = await this.rest.request({
      url: "/api/entities",
      method: "head",
    });
    return response.headers['pagination-count'] || 0;
  }

  async getIntegrationStatuses(): Promise<IntegrationStatus[]> {
    return this.rest.pagedGet(
      {
        url: "/api/intg",
        method: "get",
        params: {
          enabled: true,
        },
      },
      50
    );
  }

  async getInstances(): Promise<IntegrationInstance[]> {
    return this.rest.pagedGet(
      {
        url: "/api/intg/instances",
        method: "get",
        params: {
          enabled: true,
        },
      },
      50
    );
  }
  async getEnabledDrivers(): Promise<IntegrationDriverInfo[]> {
    return this.getDrivers({
      enabled: true,
    });
  }
  async getInstantiableDrivers(): Promise<IntegrationDriverInfo[]> {
    return this.getDrivers({
      instantiable: true,
      enabled: true,
    });
  }
  async getNotConfiguredDrivers(): Promise<IntegrationDriverInfo[]> {
    return this.getDrivers({
      has_instances: false,
      enabled: true,
    });
  }

  async getNotConfiguredCustomDrivers(): Promise<IntegrationDriverInfo[]> {
    return this.getDrivers({
      driver_type: DriverType.CUSTOM,
      has_instances: false,
      enabled: true,
    });
  }

  async getNotConfiguredExternalDrivers(): Promise<IntegrationDriverInfo[]> {
    return this.getDrivers({
      driver_type: DriverType.EXTERNAL,
      has_instances: false,
      enabled: true,
    });
  }

  protected async getDrivers(params = {}): Promise<IntegrationDriverInfo[]> {
    return await this.rest.pagedGet(
      {
        url: "/api/intg/drivers",
        method: "get",
        params,
      },
      50
    );
  }

  async getEntities(integration_id: string, filter: string, reload: boolean = false): Promise<AvailableEntity[]> {
    const params: Record<string, any> = {};
    params.reload = reload;
    if (filter && filter.length > 0) {
      params.filter = filter;
    };
    return this.rest.pagedGet(
      {
        url: `/api/intg/instances/${integration_id}/entities`,
        method: "get",
        params: params,
      },
      50
    );
  }

  async getEntitiesByPageByLimit(
    integration_id: string = '',
    reload: boolean = false,
    page: number = 1,
    limit: number = 100,
    filter: string = 'NEW',
    searchText: string = '',
    entityTypes: string = '',
  ): Promise<{data: AvailableEntity[], headers: object}> {
    const params: Record<string, any> = {};
    params.reload = reload;
    if (filter && filter.length > 0) {
      params.filter = filter;
    };
    if (searchText && searchText.length > 0) {
      params.q = searchText;
    };
    if (entityTypes && entityTypes.length > 0) {
      params.entity_types = entityTypes;
    };

    const baseGet =  this.rest.baseGet(
      {
        url: `/api/intg/instances/${integration_id}/entities`,
        method: "get",
        params: params,
      },
      limit,
      page
    );
    return baseGet
  }

  async getEntitiesByPage(integration_id: string, filter: string, reload: boolean = false, page: number = 1, searchText: string = '', entityTypes: string = ''): Promise<{data: AvailableEntity[], headers: object}> {
    const params: Record<string, any> = {};
    params.reload = reload;
    if (filter && filter.length > 0) {
      params.filter = filter;
    };
    if (searchText && searchText.length > 0) {
      params.q = searchText;
    };
    if (entityTypes && entityTypes.length > 0) {
      params.entity_types = entityTypes;
    };

    const baseGet =  this.rest.baseGet(
      {
        url: `/api/intg/instances/${integration_id}/entities`,
        method: "get",
        params: params,
      },
      50,
      page
    );
    return baseGet
  }

  async getConfiguredEntitiesByPageByLimit(
    integration_ids: string = '',
    reload: boolean = false,
    page: number = 1,
    limit: number = 100, 
    searchText: string = '',
    entityTypes: string = '',
    exclude: string = '',
  ): Promise<{data: ConfiguredEntity[], headers: object}> {
    const params: Record<string, any> = {};
    params.reload = reload;
    if (integration_ids && integration_ids.length > 0) {
      params.intg_ids = integration_ids;
    };
    if (searchText && searchText.length > 0) {
      params.q = searchText;
    };
    if (entityTypes && entityTypes.length > 0) {
      params.entity_types = entityTypes;
    };
    if (exclude && exclude.length > 0) {
      params.exclude = exclude;
    };

    const baseGet = this.rest.baseGet(
      {
        url: "/api/entities",
        method: "get",
        params: params,
      },
      limit,
      page
    );
    return baseGet
  }

  async getConfiguredEntitiesByPage(integration_ids: string = '', reload: boolean = false, page: number = 1, searchText: string = '', entityTypes: string = ''): Promise<{data: AvailableEntity[], headers: object}> {
    const params: Record<string, any> = {};
    params.reload = reload;
    if (integration_ids && integration_ids.length > 0) {
      params.intg_ids = integration_ids;
    };
    if (searchText && searchText.length > 0) {
      params.q = searchText;
    };
    if (entityTypes && entityTypes.length > 0) {
      params.entity_types = entityTypes;
    };

    const baseGet =  this.rest.baseGet(
      {
        url: "/api/entities",
        method: "get",
        params: params,
      },
      50,
      page
    );
    return baseGet
  }

  async getConfiguredEntities(
    intg_id: string | null = null
  ): Promise<ConfiguredEntity[]> {
    const params: Record<string, any> = {};
    if (intg_id) {
      params.intg_id = intg_id;
    }
    return this.rest.pagedGet(
      {
        url: "/api/entities",
        method: "get",
        params,
      },
      50
    );
  }

  async getConfiguredEntitiesByTypes(entityTypes: string): Promise<ConfiguredEntity[]> {
    const params: Record<string, any> = {};
    if (entityTypes && entityTypes.length > 0) {
      params.entity_types = entityTypes;
    };
    return this.rest.pagedGet(
      {
        url: `/api/entities`,
        method: "get",
        params: params,
      },
      50
    );
  }

  async addIntegration(
    driver_id: DriverId,
    data: NewIntegrationData
  ): Promise<IntegrationInstance> {
    const response = await this.rest.request({
      url: `/api/intg/drivers/${driver_id}`,
      method: "post",
      data,
    });
    return response.data;
  }

  async updateIntegration(
    inst_id: string,
    data: IntegrationRequest
  ): Promise<IntegrationInstance> {
    const response = await this.rest.request({
      url: `/api/intg/instances/${inst_id}`,
      method: "patch",
      data,
    });
    return response.data;
  }

  async getIntegration(inst_id: string): Promise<IntegrationInstance> {
    const response = await this.rest.request({
      method: "get",
      url: `/api/intg/instances/${inst_id}`,
    });
    return response.data;
  }

  async getDriver(driver_id: string): Promise<IntegrationDriver> {
    const response = await this.rest.request({
      method: "get",
      url: `/api/intg/drivers/${driver_id}`,
    });
    return response.data;
  }

  async addInstanceEntity(
    inst: IntegrationInstance,
    entity: AvailableEntity
  ): Promise<ConfiguredEntity> {
    const response = await this.rest.request({
      method: "post",
      url: `/api/intg/instances/${inst.integration_id}/entities/${entity.entity_id}`,
      data: {
        name: entity.name,
      },
    });
    return response.data;
  }

  async addInstanceEntities(
    inst: IntegrationInstance,
    entity_ids: string[]
  ): Promise<string[]> {
    const data = entity_ids;
    const response = await this.rest.request({
      method: "post",
      url: `/api/intg/instances/${inst.integration_id}/entities`,
      data: data
    });
    return response.data;
  }

  async getEntity(
    entity_id: string
  ): Promise<ConfiguredEntity> {
    const response = await this.rest.request({
      method: "get",
      url: `/api/entities/${entity_id}`,
    });
    return response.data;
  }

  async updateEntity(
    entity_id: string,
    changes: EntityRequest
  ): Promise<ConfiguredEntity> {
    const response = await this.rest.request({
      method: "patch",
      url: `/api/entities/${entity_id}`,
      data: changes,
    });
    return response.data;
  }

  async removeEntity(entity: ConfiguredEntity) {
    const response = await this.rest.request({
      method: "delete",
      url: `/api/entities/${entity.entity_id}`,
    });
    return response.data;
  }

  async removeEntities(entity_ids: string[]) {
    const data = {
      entity_ids: entity_ids
    }
    const response = await this.rest.request({
      method: "delete",
      url: `/api/entities`,
      data: data
    });
    return response.data;
  }

  async executeEntityCommand(
    entity_id: string,
    cmd_id: string,
    params?: object
  ): Promise<Boolean> {
    const data = {
      entity_id: entity_id,
      cmd_id: cmd_id,
      ...(params && { params: params })
    };
    
    const response = await this.rest.request({
      method: "put",
      url: `/api/entities/${entity_id}/command`,
      data,
    });
    return response.data;
  }

  async browseMediaContainers(
    entity_id: string,
    page: number = 1,
    limit: number = 10,
    parameters?: object
  ): Promise<{data: {media: MediaItem}, headers: object}> {
    const params = {
      ...parameters,
      page: page,
      limit: limit
    }

    const baseGet = this.rest.baseGet(
      {
        url: `/api/entities/${entity_id}/media/browse`,
        method: "get",
        params: params,
      },
      limit,
      page
    );

    return baseGet;
  }

  async searchMediaItems(
    entity_id: string,
    page: number = 1,
    limit: number = 10,
    parameters?: object
  ): Promise<{data: MediaItem[], headers: object}> {
    const params = {
      ...parameters,
      page: page,
      limit: limit
    }

    const baseGet = this.rest.baseGet(
      {
        url: `/api/entities/${entity_id}/media/search`,
        method: "get",
        params: params,
      },
      limit,
      page
    );

    return baseGet;
  }

  async connectInst(inst_id: string): Promise<IntegrationInstance> {
    return this.changeConnectionStatus(inst_id, true);
  }
  async disconnectInst(inst_id: string): Promise<IntegrationInstance> {
    return this.changeConnectionStatus(inst_id, false);
  }
  protected async changeConnectionStatus(
    inst_id: string,
    connect: boolean
  ): Promise<IntegrationInstance> {
    await this.rest.request({
      url: `/api/intg/instances/${inst_id}`,
      method: "put",
      params: {
        cmd: connect ? "CONNECT" : "DISCONNECT",
      },
    });
    return this.getIntegration(inst_id);
  }

  async deleteIntegration(integration: IntegrationStatus): Promise<boolean> {
    const config: RawAxiosRequestConfig = {
      method: "delete",
    };
    if (integration.integration_id) {
      config.url = `/api/intg/instances/${integration.integration_id}`;
    } else if (integration.driver_id && !integration.integration_id) {
      config.url = `/api/intg/drivers/${integration.driver_id}`;
    } else {
      throw "Cannot delete integration";
    }
    const response = await this.rest.request(config);
    return response.data.code === "OK";
  }

  async updateDriver(
    driver_id: string,
    data: IntegrationDriverUpdate
  ): Promise<IntegrationDriver> {
    const response = await this.rest.request({
      method: "patch",
      url: `/api/intg/drivers/${driver_id}`,
      data,
    });
    return response.data;
  }

  async startDiscovery() {
    const response = await this.rest.request({
      method: "put",
      url: "/api/intg/discover",
    });
    return response.data.code === "OK";
  }

  async stopDiscovery() {
    const response = await this.rest.request({
      method: "delete",
      url: "/api/intg/discover",
    });
    return response.data.code === "OK";
  }

  async testConnection(
    driver_id: string,
    driver_url: string,
    token: string | undefined = undefined
  ): Promise<DriverConnectionTestResult> {
    const request = await this.rest.request({
      method: "put",
      url: `/api/intg/discover/${driver_id}`,
      params: {
        cmd: "CONNECTION_TEST",
      },
      data: {
        connection: {
          driver_url,
          token,
        },
      },
    });
    const result: DriverConnectionTestResult = {
      code: 200,
      result: false,
      message: request.data.message,
    };
    if (request.data.driver_id === driver_id) {
      result.result = true;
      result.driver = request.data;
    }
    return result;
  }

  async registerIntegration(
    driver_id: string,
    driver_url: string,
    token: string | undefined = undefined
  ): Promise<IntegrationDriver> {
    const request = await this.rest.request({
      method: "post",
      url: `/api/intg/discover/${driver_id}`,
      data: {
        driver_url,
        token,
      },
    });
    return await this.getDriver(request.data.driver_id);
  }

  async startSetupIntegration(
    driver_id: string,
    reconfigure: boolean = false,
    data: IntegrationSetupDataInput
  ): Promise<IntegrationSetupInfo> {
    const response = await this.rest.request({
      method: "post",
      url: "/api/intg/setup",
      data: {
        driver_id,
        reconfigure,
        setup_data: data.input_values || {},
      },
    });
    return response.data;
  }

  async continueSetupIntegration(
    driver_id: string,
    data: IntegrationSetupData
  ): Promise<IntegrationSetupInfo> {
    const response = await this.rest.request({
      method: "put",
      url: `/api/intg/setup/${driver_id}`,
      data,
    });
    return response.data;
  }

  async stopSetupIntegration(driver_id: string): Promise<boolean> {
    const response = await this.rest.request({
      method: "delete",
      url: `/api/intg/setup/${driver_id}`,
    });
    return response.data.code === "OK";
  }

  async importCustomIntegration(file: File, update: boolean = false): Promise<IntegrationDriverInfo> {
    return new Promise((resolve, reject) => {
      const data = new FormData();
      data.append("file", file);

      this.rest
        .request({
          method: "post",
          url: `/api/intg/install?update=${update}`,
          data,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }, maxUploadTimeout)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (this.rest.isUploadError(error)) {
            error;
          }
          reject(error);
        });
    });
  }
}
